import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "../ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { GripVertical, MapPin, Clock, Phone, Plus, SkipForward, RotateCcw, MoreHorizontal, Truck, CalendarDays, Ban, XCircle, AlertTriangle, Navigation, Pencil, Undo2, Receipt } from "lucide-react";
import { FASCE_VISITA_LABEL, MOTIVO_RECUPERO_LABEL } from "../../lib/constants";
import { buildNavigaUrl } from "./printSheet";

function StopOverrideMenu({ stop, dataGiro, currentMezzoId, allMezzi, onStopOverride, onPriorita, isDone = false }) {
  const [open, setOpen] = useState(null); // null | 'sposta_mezzo' | 'sposta_giorno' | 'salta' | 'extra' | 'urgenza'
  const [form, setForm] = useState({ mezzo_destinazione_id: "", data_destinazione: "", motivo: "" });
  const [urgMotivo, setUrgMotivo] = useState("");

  const overrides = stop.overrides || [];
  const hasActive = overrides.length > 0;

  const submitOverride = async (tipo) => {
    const payload = {
      data_giro: dataGiro,
      produttore_id: stop.id,
      tipo,
      motivo: form.motivo || "",
    };
    if (tipo === "SPOSTA_MEZZO" || tipo === "AGGIUNGI_EXTRA") {
      if (!form.mezzo_destinazione_id) return;
      payload.mezzo_destinazione_id = form.mezzo_destinazione_id;
    }
    if (tipo === "SPOSTA_GIORNO") {
      if (!form.data_destinazione) return;
      payload.data_destinazione = form.data_destinazione;
    }
    await onStopOverride("create", payload);
    setOpen(null);
    setForm({ mezzo_destinazione_id: "", data_destinazione: "", motivo: "" });
  };

  const cancelOverride = async (ovId) => {
    await onStopOverride("cancel", { overrideId: ovId });
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0 text-slate-500 hover:text-slate-900"
            data-testid={`stop-menu-${stop.id}`}
            aria-label="Menu azioni"
          >
            <MoreHorizontal size={16} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="text-xs">Override fermata</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => setOpen("sposta_mezzo")} data-testid={`override-sposta-mezzo-${stop.id}`}>
            <Truck size={14} className="mr-2" /> Sposta su altro mezzo
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setOpen("sposta_giorno")} data-testid={`override-sposta-giorno-${stop.id}`}>
            <CalendarDays size={14} className="mr-2" /> Sposta su altro giorno
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => submitOverride("SALTA_OGGI")} data-testid={`override-salta-${stop.id}`}>
            <Ban size={14} className="mr-2" /> Salta solo oggi
          </DropdownMenuItem>
          {onPriorita && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs">Priorità fermata</DropdownMenuLabel>
              {!stop.prioritaUrgente ? (
                <DropdownMenuItem
                  onClick={() => { setUrgMotivo(""); setOpen("urgenza"); }}
                  data-testid={`priorita-set-${stop.id}`}
                >
                  <AlertTriangle size={14} className="mr-2 text-[#C62828]" /> Segna come URGENTE
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={() => onPriorita("remove", { produttoreId: stop.id })}
                  className="text-[#C62828]"
                  data-testid={`priorita-remove-${stop.id}`}
                >
                  <XCircle size={14} className="mr-2" /> Rimuovi urgenza
                </DropdownMenuItem>
              )}
            </>
          )}
          {stop.isExtra && stop.extraOverrideId && !isDone
            && !(stop.extraMotivo || "").toLowerCase().startsWith("recupero") && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => onStopOverride("cancel", {
                  overrideId: stop.extraOverrideId,
                  successMsg: "Fermata extra rimossa dal giro",
                })}
                className="text-[#C62828]"
                data-testid={`rimuovi-extra-${stop.id}`}
              >
                <Ban size={14} className="mr-2" /> Rimuovi fermata extra
              </DropdownMenuItem>
            </>
          )}
          {hasActive && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] text-slate-500 uppercase">Override attivi</DropdownMenuLabel>
              {overrides.map((ov) => (
                <DropdownMenuItem
                  key={ov.id}
                  onClick={() => cancelOverride(ov.id)}
                  className="text-[#C62828]"
                  data-testid={`override-cancel-${ov.id}`}
                >
                  <XCircle size={14} className="mr-2" /> Annulla "{ov.tipo}"
                </DropdownMenuItem>
              ))}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={open === "sposta_mezzo"} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent data-testid={`dialog-sposta-mezzo-${stop.id}`}>
          <DialogHeader>
            <DialogTitle>Sposta {stop.ragioneSociale} su altro mezzo</DialogTitle>
            <DialogDescription>Il produttore verrà ritirato oggi da un mezzo diverso. Override applicato solo a questa data.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Mezzo destinazione *</Label>
              <Select value={form.mezzo_destinazione_id} onValueChange={(v) => setForm({ ...form, mezzo_destinazione_id: v })}>
                <SelectTrigger data-testid={`override-mezzo-select-${stop.id}`}><SelectValue placeholder="Seleziona mezzo" /></SelectTrigger>
                <SelectContent>
                  {allMezzi.filter((m) => m.attivo && m.id !== currentMezzoId).map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      <span className="inline-flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: m.colore }} />
                        {m.nome}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Motivo (opzionale)</Label>
              <Input value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} placeholder="es. richiesta cliente" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(null)}>Annulla</Button>
            <Button
              onClick={() => submitOverride("SPOSTA_MEZZO")}
              disabled={!form.mezzo_destinazione_id}
              className="bg-[#1B5E20] hover:bg-[#144818] text-white"
              data-testid={`override-confirm-sposta-mezzo-${stop.id}`}
            >
              Conferma
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open === "sposta_giorno"} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent data-testid={`dialog-sposta-giorno-${stop.id}`}>
          <DialogHeader>
            <DialogTitle>Sposta {stop.ragioneSociale} su altro giorno</DialogTitle>
            <DialogDescription>Il produttore non sarà visitato oggi e verrà inserito nel giro della data scelta.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Data destinazione *</Label>
              <Input
                type="date"
                value={form.data_destinazione}
                onChange={(e) => setForm({ ...form, data_destinazione: e.target.value })}
                data-testid={`override-data-${stop.id}`}
              />
            </div>
            <div>
              <Label>Motivo (opzionale)</Label>
              <Input value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} placeholder="es. richiesta cliente" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(null)}>Annulla</Button>
            <Button
              onClick={() => submitOverride("SPOSTA_GIORNO")}
              disabled={!form.data_destinazione}
              className="bg-[#1B5E20] hover:bg-[#144818] text-white"
              data-testid={`override-confirm-sposta-giorno-${stop.id}`}
            >
              Conferma
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={open === "urgenza"} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent data-testid={`dialog-urgenza-${stop.id}`}>
          <DialogHeader>
            <DialogTitle>Segna {stop.ragioneSociale} come URGENTE</DialogTitle>
            <DialogDescription>
              L'urgenza vale solo per questa fermata di questo giro: non modifica frequenza,
              settimana o anagrafica del produttore.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Motivo *</Label>
            <Input
              value={urgMotivo}
              onChange={(e) => setUrgMotivo(e.target.value)}
              placeholder="es. serbatoio pieno, richiesta cliente"
              data-testid={`urgenza-motivo-${stop.id}`}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(null)}>Annulla</Button>
            <Button
              onClick={async () => {
                await onPriorita("set", { produttoreId: stop.id, motivo: urgMotivo.trim() });
                setOpen(null);
              }}
              disabled={!urgMotivo.trim()}
              className="bg-[#C62828] hover:bg-[#A31F1F] text-white"
              data-testid={`urgenza-confirm-${stop.id}`}
            >
              Conferma urgenza
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function SortableStop({
  stop, index, color, onRegister, onSkip, isDone,
  onStopOverride, dataGiro, currentMezzoId, allMezzi = [], isCarrier = false,
  disableReorder = false, onPriorita, isNext = false, showNaviga = false,
  canManageRitiro = false, onEditRitiro, onCancelRitiro,
  giroChiuso = false, recuperoCreato = false,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: stop.id, disabled: disableReorder || giroChiuso });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  const navigaUrl = showNaviga && !isDone ? buildNavigaUrl(stop) : null;
  const ovrs = stop.overrides || [];
  const hasOverride = ovrs.length > 0;
  // B5.5 — Distingui RECUPERO (AGGIUNGI_EXTRA con motivo "Recupero…") da EXTRA generico e da OVERRIDE.
  const isRecupero = ovrs.some(
    (o) => o.tipo === "AGGIUNGI_EXTRA" && (o.motivo || "").toLowerCase().startsWith("recupero"),
  );
  const isExtra = !isRecupero && ovrs.some((o) => o.tipo === "AGGIUNGI_EXTRA");
  return (
    <div ref={setNodeRef} style={style} className="bg-white border border-slate-200 rounded-xl p-3 flex items-start gap-2" data-testid={`stop-${stop.id}`}>
      {!disableReorder && !isDone && !giroChiuso && (
        <button {...attributes} {...listeners} className="touch-none text-slate-400 hover:text-slate-700 mt-1 min-h-[44px] flex items-center" aria-label="Trascina" data-testid={`drag-handle-${stop.id}`}>
          <GripVertical size={18} />
        </button>
      )}
      <div className="h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0 mt-0.5"
           style={{ backgroundColor: color, fontFamily: "Outfit, sans-serif" }}>{index + 1}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1 flex-wrap">
          {!isCarrier ? (
            <Link
              to={`/produttori/${stop.id}`}
              className="font-semibold text-slate-900 truncate hover:text-[#1565C0] hover:underline underline-offset-2"
              style={{ fontFamily: "Outfit, sans-serif" }}
              data-testid={`stop-prodlink-${stop.id}`}
              title="Apri scheda produttore"
              onClick={(e) => e.stopPropagation()}
            >
              {stop.ragioneSociale}
            </Link>
          ) : (
            <div className="font-semibold text-slate-900 truncate" style={{ fontFamily: "Outfit, sans-serif" }}>{stop.ragioneSociale}</div>
          )}
          {isNext && (
            <Badge
              className="bg-[#0D9488] text-white text-[10px]"
              data-testid={`prossima-consigliata-${stop.id}`}
              title="Suggerimento: prossima fermata secondo l'ordine del giro (non vincolante)"
            >
              <Navigation size={10} className="mr-0.5" /> PROSSIMA CONSIGLIATA
            </Badge>
          )}
          {stop.prioritaUrgente && (
            <Badge
              data-testid={`urgente-fermata-badge-${stop.id}`}
              className="bg-[#C62828] text-white text-[10px]"
              title={stop.urgenza?.motivo || "Fermata urgente"}
            >
              <AlertTriangle size={10} className="mr-0.5" /> URGENTE
            </Badge>
          )}
          {isDone && <Badge data-testid={`done-badge-${stop.id}`} className="bg-[#1B5E20] text-white text-[10px]">RITIRATO</Badge>}
          {giroChiuso && !isDone && recuperoCreato && (
            <Badge data-testid={`recupero-creato-badge-${stop.id}`} className="bg-[#7B1FA2] text-white text-[10px]">
              RECUPERO CREATO
            </Badge>
          )}
          {isRecupero && <Badge data-testid={`recupero-stop-${stop.id}`} className="bg-[#7B1FA2] text-white text-[10px]">RECUPERO</Badge>}
          {isExtra && <Badge data-testid={`extra-stop-${stop.id}`} className="bg-[#F57C00] text-white text-[10px]">EXTRA</Badge>}
          {hasOverride && !isRecupero && !isExtra && <Badge data-testid={`override-stop-${stop.id}`} className="bg-[#1565C0] text-white text-[10px]">OVERRIDE</Badge>}
          {(() => {
            const ds = stop.contratto?.dataScadenza;
            if (!ds) return null;
            const today = new Date(); today.setHours(0,0,0,0);
            const d = new Date(ds); if (isNaN(d.getTime())) return null;
            d.setHours(0,0,0,0);
            const giorni = Math.round((d.getTime() - today.getTime()) / 86400000);
            if (giorni < 0) return <Badge data-testid={`badge-scaduto-${stop.id}`} className="bg-[#7F1D1D] text-white text-[10px]">🚨 SCADUTO</Badge>;
            if (giorni <= 30) return <Badge data-testid={`badge-urgente-${stop.id}`} className="bg-[#C62828] text-white text-[10px]">⚠️ {giorni}gg</Badge>;
            return null;
          })()}
        </div>
        <div className="text-xs text-slate-500 truncate flex items-center gap-1"><MapPin size={11} /> {stop.indirizzo}</div>
        <div className="text-xs text-slate-600 truncate flex items-center gap-2 mt-1">
          <Clock size={11} /> {stop.orarioRitiro || "-"}
          {stop.fasciaOrariaVisita && stop.fasciaOrariaVisita !== "SEMPRE_APERTO" && stop.fasciaOrariaVisita !== "DA_VERIFICARE" && (
            <Badge
              variant="outline"
              className="border-[#1565C0]/60 text-[#1565C0] text-[10px]"
              data-testid={`fascia-visita-stop-${stop.id}`}
            >
              {FASCE_VISITA_LABEL[stop.fasciaOrariaVisita] || stop.fasciaOrariaVisita}
            </Badge>
          )}
          {stop.fasciaOrariaVisita === "DA_VERIFICARE" && (
            <Badge
              variant="outline"
              className="border-[#C62828] text-[#C62828] text-[10px]"
              data-testid={`fascia-visita-stop-${stop.id}`}
            >
              Orario da verificare
            </Badge>
          )}
          {stop.fasciaDaVerificare && stop.fasciaOrariaVisita !== "DA_VERIFICARE" && (
            <Badge variant="outline" className="border-[#F57C00] text-[#F57C00] text-[10px]">DA VERIFICARE</Badge>
          )}
        </div>
        {stop.prioritaUrgente && stop.urgenza && (
          <div className="text-[11px] text-[#C62828] font-medium mt-0.5" data-testid={`urgenza-info-${stop.id}`}>
            Urgenza: {stop.urgenza.motivo}
            {stop.urgenza.autoreNome ? ` — ${stop.urgenza.autoreNome}` : ""}
            {stop.urgenza.dataInserimento
              ? ` (${new Date(stop.urgenza.dataInserimento).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })})`
              : ""}
          </div>
        )}
        {(stop.telefonoFisso || stop.telefonoMobile) && (
          <a href={`tel:${stop.telefonoMobile || stop.telefonoFisso}`} className="text-xs text-[#1565C0] flex items-center gap-1 mt-1">
            <Phone size={11} /> {stop.telefonoMobile || stop.telefonoFisso}
          </a>
        )}
        {stop.partitaIva && (
          <div className="text-xs text-slate-500 truncate flex items-center gap-1 mt-0.5" data-testid={`piva-${stop.id}`}>
            <Receipt size={11} /> P.IVA: {stop.partitaIva}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1 shrink-0 items-end">
        {!isCarrier && onStopOverride && !giroChiuso && (
          <StopOverrideMenu
            stop={stop}
            dataGiro={dataGiro}
            currentMezzoId={currentMezzoId}
            allMezzi={allMezzi}
            onStopOverride={onStopOverride}
            onPriorita={onPriorita}
            isDone={isDone}
          />
        )}
        {!isDone && !giroChiuso && (
          <Button size="sm" onClick={() => onRegister(stop)} className="bg-[#1B5E20] hover:bg-[#144818] text-white text-xs min-h-[40px]" data-testid={`register-${stop.id}`}>
            <Plus size={14} className="mr-1" />Ritiro
          </Button>
        )}
        {!isDone && !giroChiuso && (
          <Button size="sm" variant="outline" onClick={() => onSkip(stop)} className="text-[#F57C00] border-[#F57C00] hover:bg-[#F57C00]/10 text-xs min-h-[36px] py-1" data-testid={`skip-${stop.id}`}>
            <SkipForward size={12} className="mr-1" />Salta
          </Button>
        )}
        {isDone && canManageRitiro && !giroChiuso && (
          <>
            <Button
              size="sm" variant="outline"
              onClick={() => onEditRitiro && onEditRitiro(stop)}
              className="text-[#1565C0] border-[#1565C0] hover:bg-[#1565C0]/10 text-xs min-h-[36px] py-1"
              data-testid={`edit-ritiro-${stop.id}`}
              title="Modifica quantità e note del ritiro"
            >
              <Pencil size={12} className="mr-1" />Modifica ritiro
            </Button>
            <Button
              size="sm" variant="outline"
              onClick={() => onCancelRitiro && onCancelRitiro(stop)}
              className="text-[#C62828] border-[#C62828] hover:bg-[#C62828]/10 text-xs min-h-[36px] py-1"
              data-testid={`annulla-ritiro-${stop.id}`}
              title="Annulla il ritiro: la fermata torna pendente"
            >
              <Undo2 size={12} className="mr-1" />Annulla ritiro
            </Button>
          </>
        )}
        {navigaUrl && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => window.open(navigaUrl, "_blank", "noopener")}
            className="text-[#1565C0] border-[#1565C0] hover:bg-[#1565C0]/10 text-xs min-h-[36px] py-1"
            data-testid={`naviga-${stop.id}`}
            title="Apri la navigazione Google Maps verso questa fermata (non modifica il giro)"
          >
            <Navigation size={12} className="mr-1" />Naviga
          </Button>
        )}
      </div>
    </div>
  );
}

export function SkippedStop({ stop, onRestore, onCreaRecupero, giroChiuso = false, isCarrier = false }) {
  return (
    <div className="bg-[#F57C00]/5 border border-[#F57C00]/30 rounded-xl p-3 flex items-start gap-2" data-testid={`skipped-${stop.id}`}>
      <div className="h-8 w-8 rounded-full bg-[#F57C00]/20 flex items-center justify-center text-[#F57C00] shrink-0 mt-0.5">
        <SkipForward size={14} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-semibold text-slate-700 truncate line-through" style={{ fontFamily: "Outfit, sans-serif" }}>{stop.ragioneSociale}</div>
          <Badge className="bg-[#F57C00] text-white text-[10px]">SALTATA</Badge>
          {giroChiuso && (
            <Badge className="bg-slate-600 text-white text-[10px]" data-testid={`giro-chiuso-badge-${stop.id}`}>
              GIRO CHIUSO
            </Badge>
          )}
        </div>
        <div className="text-xs text-slate-500 truncate"><MapPin size={11} className="inline -mt-0.5 mr-1" />{stop.indirizzo}</div>
        {stop.motivoSaltata && (
          <div className="text-[11px] text-[#F57C00] font-medium mt-0.5" data-testid={`skip-motivo-label-${stop.id}`}>
            Motivo: {MOTIVO_RECUPERO_LABEL[stop.motivoSaltata] || stop.motivoSaltata}
            {stop.motivoDettaglio ? ` — ${stop.motivoDettaglio}` : ""}
          </div>
        )}
      </div>
      {!giroChiuso && (
        <Button size="sm" variant="outline" onClick={() => onRestore(stop)} className="text-xs min-h-[40px]" data-testid={`restore-${stop.id}`}>
          <RotateCcw size={12} className="mr-1" />Ripristina
        </Button>
      )}
      {giroChiuso && !isCarrier && onCreaRecupero && (
        <Button
          size="sm"
          variant="outline"
          onClick={() => onCreaRecupero(stop)}
          className="text-xs min-h-[40px] border-[#7B1FA2] text-[#7B1FA2] hover:bg-[#7B1FA2]/10"
          data-testid={`crea-recupero-saltata-${stop.id}`}
        >
          <RotateCcw size={12} className="mr-1" />Crea recupero
        </Button>
      )}
    </div>
  );
}
